import { useNavigate, useParams } from "react-router-dom";
import { PACOCA_URL, READBOOKS_API_URL } from "../../../global-components/src/utils/vars";
import { ChangeEvent, useEffect, useState } from "react";
import axios from "axios";
import { useAuth, useMessage } from "../../../global-components/src";
import { errorMessage } from "../../../global-components/src/utils/text";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import NotFound from "../../errors/NotFound";
import { BookOpenText } from "lucide-react";
import { isMyLove } from "../../../global-components/src/utils/auth";
import "./style.css";
import Screen from "../../../global-components/src/components/screen/screen";
import Spinner, { LoadingThreeCircle } from "../../../global-components/src/components/spinner/spiner";
import { formatDate, toDbFormat } from "../../../utils/utils";

interface BookData {
  id_livro: number;
  id_livro_google?: string;
  nome_livro: string;
  img_livro: string;
  lido: string;
  total_paginas: number;
  paginas_lidas: number;
  tempo_lido: string;
  tempo_leitura?: boolean;
  data_inicio: string;
  data_termino: string;
  descricao_livro: string;
  catalog_book_id: string;
  show_in_pacoca: boolean;
}

export function EditBook() {
    const { uuid } = useParams();
    const { token,user } = useAuth();
    const [form, setForm] = useState<BookData| null>(null);
    const [book, setBook] = useState<BookData| null>(null);
    const { setMessage } = useMessage();
    const [loading, setLoading] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [errorLoadData, setErrorLoadData] = useState(false);
    const navigate = useNavigate();
    const [errorsInput, setErrorsInput] = useState<any>({});
    const [calcularTempoLido, setCalcularTempoLido] = useState(true);
    const [errorStatusCode, setErrorStatusCode] = useState<404 | null>(null);
    const canEdittPage = !form?.catalog_book_id && !form?.id_livro_google; 

    const calcularTempoEntreDatas = (inicio: string, termino: string) => {
        const dataInicio = new Date(inicio);
        const dataTermino = new Date(termino);

        if (isNaN(dataInicio.getTime()) || isNaN(dataTermino.getTime())) {
            return '';
        }

        const diffMs = Math.abs(dataTermino.getTime() - dataInicio.getTime());
        const diffDias = Math.ceil(diffMs / (1000 * 60 * 60 * 24)) + 1;
        return `${diffDias} dia${diffDias > 1 ? 's' : ''}`;
    };


    const loadData = async () => {
        try {
            setIsLoadingData(true);
          const response = await axios.get(`${READBOOKS_API_URL}/books/${uuid}`, {
              headers: {
                  Authorization: `Bearer ${token}`
              }
          });

          setBook({
            ...response.data.book,
            data_inicio: toDbFormat(response?.data?.book?.data_inicio),
            data_termino: toDbFormat(response?.data?.book?.data_termino)
          });
          setForm({
            ...response.data.book,
            data_inicio: toDbFormat(response?.data?.book?.data_inicio),
            data_termino: toDbFormat(response?.data?.book?.data_termino)
          });
        } catch (err: any) {
          console.log(err.response?.status);
          
          setErrorStatusCode(err.response?.status || 500);
          if(err.response?.status !== 404) {
            setErrorLoadData(true)
            setLoading(false);
            const messageError = errorMessage(err);
            setMessage(messageError, "error");
            console.error("Erro ao pesquisar: ", messageError);
            console.error("Erro ao pesquisar: ", err);
          }
        } finally {
        setIsLoadingData(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if(loading) return;
        setLoading(true);

        try {
            let errors: Record<string, string[]> = {};
      
            if (!form?.nome_livro) {
                errors.nome_livro = ["O nome do livro é obrigatório."];
            }
            if (!form?.descricao_livro) {
                errors.descricao_livro = ["A descrição do livro é obrigatória."];
            }
            if (!form?.total_paginas) {
                errors.total_paginas = ["O total de páginas é obrigatório."];
            }
            if (!form?.paginas_lidas) {
                errors.paginas_lidas = ["O total de páginas lidas é obrigatório."];
            }
            if (!form?.tempo_lido) {
                errors.tempo_lido = ["O tempo lido é obrigatório."];
            }
            if (!isNaN(Number(form?.paginas_lidas)) && !isNaN(Number(form?.total_paginas)) && Number(form?.paginas_lidas) > Number(form?.total_paginas)) {
                errors.paginas_lidas = ["O número de páginas lidas não pode ser maior que o total de páginas."];
            } else if (form?.paginas_lidas === form?.total_paginas) {
                const novoLido = !isNaN(Number(form?.paginas_lidas)) && !isNaN(Number(form?.total_paginas)) && Number(form?.paginas_lidas) == Number(form?.total_paginas) ? 'sim' : 'não';
                setForm(prev => prev ? { ...prev, lido: novoLido } : prev);
            }

            if (Object.keys(errors).length > 0) {
                throw {
                    response: {
                        data: {
                            errors
                        }
                    }
                };
            }

            const config = {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            };

            const url = `${READBOOKS_API_URL}/books${uuid ? `/${uuid}` : ''}`;
            const response = await axios.post(url, form, config);
            if (user?.id) {
                localStorage.removeItem(`ai_reading_analysis_${user.id}`);
            }
            localStorage.removeItem("ai_reading_analysis");
            setMessage(response.data.message, "success");
            navigate(`/livros/${uuid}`);
        } catch (err: any) {
            if (err?.response?.data.errors) {
                console.error(err?.response?.data.errors);
                setErrorsInput(err.response.data.errors); // Atualiza o estado com os erros
                return;
            }
            
            const messageError = errorMessage(err);
            setMessage(messageError, "error");
            console.error("Erro ao pesquisar: ", messageError);
            console.error("Erro ao pesquisar: ", err);
        } finally {
        setLoading(false);
        }
    };


    useEffect(() => {
        if (uuid) {
        loadData();
        }
    }, [uuid]);

    const handleChange = (
  e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
) => {
  const target = e.target as HTMLInputElement | HTMLTextAreaElement;
  const { name, value, type } = target;
  const checked = (target as HTMLInputElement).checked;

  if (name === 'tempo_leitura') {
    setCalcularTempoLido(checked);

    setForm((prev) => {
      const updated = { ...prev } as BookData;
      if (checked && updated?.data_inicio && updated?.data_termino) {
        updated.tempo_lido = calcularTempoEntreDatas(updated.data_inicio, updated.data_termino);
      }
      return updated;
    });

    return;
  }

  setForm((prev) => {
    const updated = {
      ...(prev ?? {
        id_livro: 0,
        nome_livro: "",
        img_livro: "",
        lido: "",
        total_paginas: 0,
        paginas_lidas: 0,
        tempo_lido: "",
        data_inicio: "",
        data_termino: "",
        descricao_livro: "",
        show_in_pacoca: false,
        id_livro_google: "",
        catalog_book_id: ""
      }),
      [name]: type === "checkbox" ? checked : value,
    };

    // Recalcular tempo_lido automaticamente se for marcado e datas válidas
    if (
      calcularTempoLido &&
      (name === 'data_inicio' || name === 'data_termino') &&
      updated.data_inicio &&
      updated.data_termino
    ) {
      updated.tempo_lido = calcularTempoEntreDatas(updated.data_inicio, updated.data_termino);
    }

    return updated;
  });
};

  if (errorStatusCode === 404) {
      return (
          <NotFound text="Livro não encontrado" showLink={false}>
              <BookOpenText className="icon-empty"/>
          </NotFound>
      );
  }

  return (
    <>
      <div className="margin-top-menu container container-edit-book pt-3">
        <h2 className="text-center mb-3">Editar as informações do livro{isMyLove(user) ? ", meu amor" : ""}</h2>
        <p className="text-center">
          {form?.nome_livro ?? ""}
        </p>

        <form className="" onSubmit={handleSubmit}>
            <div className="d-flex justify-content-center">
              {isLoadingData && <LoadingThreeCircle className="text-center" />}
            </div>

            <div className="mb-3">
              <label htmlFor="nome_livro" className="label">Nome do Livro</label>
              <input disabled={isLoadingData || errorLoadData || (!canEdittPage && !!book?.nome_livro)} type="text" onChange={handleChange} className={`form-control ${errorsInput.nome_livro ? 'is-invalid' : ''}`} id="nome_livro" name="nome_livro" value={form?.nome_livro ?? ""} />
              {errorsInput.nome_livro && (
                  <div className="invalid-feedback" style={{ display: "block" }}>{errorsInput.nome_livro[0]}</div> // Exibe a mensagem de erro
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="total_paginas" className="label">Total de Páginas</label>
              <input disabled={isLoadingData || errorLoadData || (!canEdittPage && !!book?.total_paginas)} type="number" onChange={handleChange} className={`form-control ${errorsInput.total_paginas ? 'is-invalid' : ''}`} id="total_paginas" name="total_paginas" value={form?.total_paginas ?? ""} />
              {errorsInput.total_paginas && (
                  <div className="invalid-feedback" style={{ display: "block" }}>{errorsInput.total_paginas[0]}</div> // Exibe a mensagem de erro
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="paginas_lidas" className="label">Páginas Lidas</label>
              <input disabled={isLoadingData || errorLoadData} type="number" onChange={handleChange} className={`form-control ${errorsInput.paginas_lidas ? 'is-invalid' : ''}`} id="paginas_lidas" name="paginas_lidas" value={form?.paginas_lidas ?? ""} />
              {errorsInput.paginas_lidas && (
                  <div className="invalid-feedback" style={{ display: "block" }}>{errorsInput.paginas_lidas[0]}</div> // Exibe a mensagem de erro
              )}
            </div>

            {/* <div className="mb-4">
              <input disabled={isLoadingData || errorLoadData} onChange={handleChange} className="form-check-input" type="checkbox" id="tempo_leitura" defaultChecked />
              <label className="form-check-label" htmlFor="tempo_leitura">Calcular tempo de leitura</label>
              <div id="passwordHelpBlock" className="form-text">
                Deixe esse campo em branco para colocar o tempo de leitura manualmente
            </div>
            </div> */}

            <div className="mb-4">
              <input disabled={isLoadingData || errorLoadData} onChange={handleChange} className="form-check-input" type="checkbox" name="show_in_pacoca" id="show_in_pacoca" checked={form?.show_in_pacoca ?? true} />
              <label className="form-check-label" htmlFor="show_in_pacoca">
                Mostrar no Paçoca
              </label>
              <div className="text-small" style={{opacity: ".7", fontSize: "0.8rem"}}>
                Deixe esse campo em branco para não mostrar o livro no seu perfil do <a target="_BLANK" href={PACOCA_URL}>Paçoca</a>
            </div>
            </div>

            <div className="mb-4 row">
              <div className="col">
                <label htmlFor="data_inicio" className="label">Data de início</label>
                <input disabled={isLoadingData || errorLoadData} onChange={handleChange} id="data_inicio" name="data_inicio" type="date" className={`form-control ${errorsInput.data_inicio ? 'is-invalid' : ''}`} value={form?.data_inicio ?? ""} />
                <div className="text-small" style={{opacity: ".7", fontSize: "0.8rem"}}>
                    Deixe esse campo em branco para o livro ser adicionado na lista de desejos. Preecnha esse campo se você já começou a ler esse livro
                </div>
                {errorsInput.data_inicio && (
                    <div className="invalid-feedback" style={{ display: "block" }}>{errorsInput.data_inicio[0]}</div> // Exibe a mensagem de erro
                )}
              </div>
              <div className="col">
                <label htmlFor="data_termino" className="label">Data de término</label>
                <input disabled={isLoadingData || errorLoadData} onChange={handleChange} id="data_termino" name="data_termino" type="date" className={`form-control ${errorsInput.data_termino ? 'is-invalid' : ''}`} value={form?.data_termino ?? ""} />
                {errorsInput.data_termino && (
                    <div className="invalid-feedback" style={{ display: "block" }}>{errorsInput.data_termino[0]}</div> // Exibe a mensagem de erro
                )}
              </div>
            </div>

            
            <div className="mb-4">
              <label htmlFor="tempo_lido" className="label">Tempo lido</label>
              <input disabled={isLoadingData || errorLoadData} onChange={handleChange} type="text" className={`form-control ${errorsInput.tempo_lido ? 'is-invalid' : ''}`} id="tempo_lido" name="tempo_lido" value={form?.tempo_lido ?? ""} />
              {errorsInput.tempo_lido && (
                  <div className="invalid-feedback" style={{ display: "block" }}>{errorsInput.tempo_lido[0]}</div> // Exibe a mensagem de erro
              )}
            </div>
  
            <div className="mb-3">
              <label htmlFor="descricao_livro" className="label">Descrição do Livro</label>
              {/* <textarea onChange={handleChange} className={`form-control ${errorsInput.descricao_livro ? 'is-invalid' : ''}`} id="descricao_livro" name="descricao_livro" rows={3} value={form?.descricao_livro ?? ""}></textarea> */}
              <CKEditor
                editor={ClassicEditor as any}
                data={form?.descricao_livro ?? ""}
                config={{
                  toolbar: [
                    'undo', 'redo', // ações básicas
                    '|',
                    'bold', 'italic',
                    '|',
                    'bulletedList', 'numberedList', // listas
                  ],
                }}
                disabled={isLoadingData || errorLoadData || (!canEdittPage && !!book?.descricao_livro)}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  setForm(prev => prev ? { ...prev, descricao_livro: data } : null);
                }} 
              />
              {errorsInput.descricao_livro && (
                    <div className="invalid-feedback" style={{ display: "block" }}>{errorsInput.descricao_livro[0]}</div> // Exibe a mensagem de erro
                )}
            </div>

            <div className="d-flex justify-content-end">
              <button disabled={loading || errorLoadData || isLoadingData} type="submit" className="btn btn-blue btn-not-100 mt-3">
                {loading ? <Spinner className="text-white"/> : ""}
                Atualizar
            </button>
            </div>
          </form>
          </div>
    </>
  )
}